import React, { useState, useEffect, useRef, useMemo } from 'react';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './EmployeeTracking.css';
import jsPDF from "jspdf";
import "jspdf-autotable";
import ExcelJS from 'exceljs';
import { selectStyles } from '../../../assets/Styles/selectStyle';
import { getDistrict, getProject, getStates, fetchMonths, getEmployeeSummaryDrilldownFullDetails } from '../../../services/monthlyAttendanceService';
import { capitalizeFirstLetter } from '../../../utils/validations';
import { getPostedAndRelievedCount, getPostedAndRelievedCountDrilldown, getPostedAndRelievedCountDistDrilldown, getTrackingDetails } from '../../../services/employeePostingService';
import { format } from 'date-fns';
import {
    BarChart,
    Bar, ScatterChart, Scatter, XAxis, YAxis, CartesianGrid,
    Tooltip, ResponsiveContainer, Cell, ZAxis
} from 'recharts';

const EmployeeTracking = () => {
    const currentYear = new Date().getFullYear();
    const [state, setState] = useState(3);
    const [district, setDistrict] = useState(null);
    const [date, setDate] = useState('');
    const [stateOptions, setStateOptions] = useState([]);
    const [districtOptions, setDistrictOptions] = useState([]);

    const [attendanceData, setAttendanceData] = useState([]);


    const effectRan = useRef(false);
    const [isFocused, setIsFocused] = useState({
        state: false,
        district: false,

        date: false,

    });
    const [errors, setErrors] = useState({});
    const yearInputRef = useRef(null);


    const isFetchDataEnabled = state && district && date;




    // Fetch states
    useEffect(() => {
        const fetchStates = async () => {
            try {
                const data = await getStates();
                setStateOptions(
                    data.map(item => ({
                        value: item.id,
                        label: capitalizeFirstLetter(item.stateName),
                    }))
                );
            } catch (error) {
                console.error('Error fetching states:', error);
                setErrors(prev => ({ ...prev, general: 'Failed to load states' }));
            }
        };
        if (!effectRan.current) {
            fetchStates();
        }
        return () => {
            effectRan.current = true;
        }
    }, []);






    // Fetch districts when state changes
    useEffect(() => {
        if (state) {
            const fetchDistricts = async () => {
                try {
                    const data = await getDistrict(state);
                    setDistrictOptions(
                        data.map(item => ({
                            value: item.districtId,
                            label: item.districtName,
                        }))
                    );
                } catch (error) {
                    console.error('Error fetching districts:', error);
                    setErrors(prev => ({ ...prev, general: 'Failed to load districts' }));
                }
            };
            // if (!effectRan.current) {
            fetchDistricts();
            // }
            // return () => {
            //     effectRan.current = true;
            // }

        } else {
            setDistrictOptions([]);
            setDistrict(null);
        }
    }, [state]);





    // Clear attendance data and fetch status when any dropdown changes
    useEffect(() => {
        setAttendanceData([]);

    }, [state, district,]); // ✅ Add project to depende

    const handleInputChange = (selectedOption, field) => {
        const value = selectedOption ? selectedOption.value : null;
        switch (field) {
            case 'state':
                setState(value);
                setDistrict(null);


                break;
            case 'district':
                setDistrict(value);
                // setProject(null); // ✅ Clear project when district changes
                break;


            default:
                break;
        }
        setErrors(prev => ({ ...prev, [field]: null }));
    };

    const handleYearChange = (date) => {
        setDate(date);
        setErrors(prev => ({ ...prev, date: null }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!state) newErrors.state = 'State is required';
        if (!district) newErrors.district = 'District is required';
        if (!date) newErrors.date = 'Year is required';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        try {
            const data = await getTrackingDetails(state, district, date,)

            const employeeData = data || [];

            setAttendanceData(employeeData);

        } catch (error) {
            console.error("Error fetching reportrarel data:", error);
            setErrors((prev) => ({
                ...prev,
                general: "Failed to fetch reportrarel data",
            }));
            alert("Failed to fetch reportrarel data. Please try again.");
        }
    };





    // const currentYear = new Date().getFullYear();
    const minDate = new Date(2000, 0, 1);
    const maxDate = new Date(currentYear, 11, 31);



    // Transform data: one point per (employee, slot)
    const buildScatterData = (attendanceData) => {
        const points = [];
        attendanceData.forEach((emp, empIdx) => {
            emp.timeline?.forEach((hourBlock, hourIdx) => {
                hourBlock.slots?.forEach((slot, slotIdx) => {
                    const slotIndex = hourIdx * 2 + slotIdx; // 0..47
                    const records = slot.records || [];
                    const has = records.length > 0;
                    const dist = records.reduce(
                        (s, r) => s + (r.distanceFromPreviousMeters || 0), 0
                    );

                    points.push({
                        empIdx,
                        empName: emp.employeeName,
                        designation: emp.designation,
                        status: emp.attendanceStatus,
                        slotIndex,          // X position (0..47)
                        empPosition: empIdx,// Y position
                        has,
                        dist,
                        timeLabel: slot.timeWindow,
                        recordCount: records.length,
                    });
                });
            });
        });
        return points;
    };
    const scatterData = useMemo(
        () => buildScatterData(attendanceData),
        [attendanceData]
    );

    const empNames = useMemo(
        () =>
            attendanceData.map(
                (e, i) => `${i + 1}. ${e.employeeName} (${e.designation || '—'})`
            ),
        [attendanceData]
    );



    return (
        <div className="tracking-reportrarel-container">
            <div className="tracking-reportrarel-box">
                <h2 className="tracking-reportrarel-heading">Employee Tracking</h2>
                {errors.general && <div className="error-message">{errors.general}</div>}
                <form onSubmit={handleSubmit}
                >
                    <div className="tracking-reportrarel-maincontainer">
                        <div className="tracking-reportrarel-container-part">
                            <div className={`tracking-reportrarel-floating-label-container ${isFocused.state || state ? 'float-label' : ''}`}>
                                <Select
                                    name="state"
                                    options={stateOptions}
                                    value={stateOptions.find(opt => opt.value === state) || null}
                                    onChange={option => handleInputChange(option, 'state')}
                                    onFocus={() => setIsFocused(prev => ({ ...prev, state: true }))}
                                    onBlur={() => setIsFocused(prev => ({ ...prev, state: false }))}
                                    placeholder=" "
                                    isClearable
                                    menuPortalTarget={document.body}
                                    styles={selectStyles}

                                />
                                <label><span className="required">*</span>State</label>
                                {errors.state && <div className="error-message">{errors.state}</div>}
                            </div>
                        </div>
                        <div className="tracking-reportrarel-container-part">
                            <div className={`tracking-reportrarel-floating-label-container ${isFocused.district || district ? 'float-label' : ''}`}>
                                <Select
                                    name="district"
                                    options={districtOptions}
                                    value={districtOptions.find(opt => opt.value === district) || null}
                                    onChange={option => handleInputChange(option, 'district')}
                                    onFocus={() => setIsFocused(prev => ({ ...prev, district: true }))}
                                    onBlur={() => setIsFocused(prev => ({ ...prev, district: false }))}
                                    placeholder=" "
                                    isClearable
                                    menuPortalTarget={document.body}
                                    styles={selectStyles}

                                />
                                <label> <span className="required">*</span>District</label>
                                {/* {errors.district && <div className="error-message">{errors.district}</div>} */}
                            </div>
                        </div>
                    </div>

                    <div className="tracking-reportrarel-maincontainer">

                        <div className="tracking-reportrarel-container-part">
                            <div className={`tracking-reportrarel-floating-label-container ${isFocused.date || date ? 'float-label' : ''}`}>
                                <DatePicker
                                    selected={date}
                                    onChange={(date) => handleYearChange(date)}
                                    onFocus={() => setIsFocused(prev => ({ ...prev, date: true }))}
                                    onBlur={() => setIsFocused(prev => ({ ...prev, date: false }))}
                                    dateFormat="dd-MM-yyyy"
                                    // showYearPicker
                                    placeholderText=""
                                    className="tracking-reportrarel-date-picker"
                                    minDate={minDate}
                                    maxDate={maxDate}
                                    ref={yearInputRef}

                                    title="Select a date"
                                />
                                <label><span className="required">*</span>Date</label>
                                {errors.date && <div className="error-message">{errors.date}</div>}
                            </div>
                        </div>
                        <div className="tracking-reportrarel-container-part">

                        </div>

                    </div>



                    <div className="tracking-reportrarel-btn-div">
                        <button type="submit" className="tracking-reportrarel-btn"
                            disabled={!isFetchDataEnabled}


                        >
                            Generate Report
                        </button>
                    </div>
                </form>
            </div>
            {attendanceData.length > 0 && (
                <div className="reportrarel-chart-container">
                    <h3 className="reportrarel-chart-title">
                        Hourly Distance Deviation by Employee
                    </h3>

                    <ResponsiveContainer
                        width="100%"
                        height={Math.max(700, attendanceData.length * 40)}
                    >
                        <ScatterChart
                            margin={{ top: 30, right: 60, left: 90, bottom: 100 }}
                        >
                            <CartesianGrid strokeDasharray="3 3" stroke="#ececec" />

                            {/* X = 48 half-hour slots */}
                            <XAxis
                                type="number"
                                dataKey="empPosition"
                                domain={[-0.5, attendanceData.length - 0.5]}
                                ticks={attendanceData.map((_, i) => i)}
                                tickFormatter={(v) => empNames[v] || ''}
                                interval={0}
                                width={180}
                                angle={-30}
                                textAnchor="end"
                                height={140}
                                tick={{ fontSize: 12, fill: '#1f2937', fontWeight: 500 }}
                                label={{
                                    value: 'Employees',
                                    position: 'insideBottom',      // ← anchors to bottom edge
                                    offset: -10,                  // ← push further down (negative = down)
                                    style: {
                                        fontSize: 13,
                                        fill: '#374151',
                                        fontWeight: 600,
                                        textAnchor: 'middle',      // ← 'middle' = horizontally centered
                                    },
                                }}
                            />

                            {/* Y = employees */}
                            <YAxis
                                type="number"
                                dataKey="slotIndex"
                                domain={[-0.5, 47.5]}
                                ticks={Array.from({ length: 24 }, (_, i) => i * 2)}
                                tickFormatter={(v) => {
                                    const h = Math.floor(v / 2);
                                    return `${String(h).padStart(2, '0')}:00`;
                                }}
                                interval={0}
                                width={70}
                                tick={{ fontSize: 11, fill: '#374151', fontWeight: 500 }}
                                label={{
                                    value: 'Time of Day',
                                    angle: -90,
                                    position: 'insideLeft',
                                    offset: 10,
                                    style: { fontSize: 13, fill: '#374151', fontWeight: 600, textAnchor: 'middle' },
                                }}
                            />

                            <ZAxis range={[80, 80]} />

                            <Tooltip
                                cursor={{ strokeDasharray: '3 3' }}
                                content={({ active, payload }) => {
                                    if (!active || !payload?.length) return null;
                                    const p = payload[0].payload;
                                    const hasRecords = p.recordCount > 0;

                                    return (
                                        <div className="reportrarel-chart-tooltip">
                                            <div className="tooltip-title">{p.empName}</div>
                                            <div className="tooltip-sub">
                                                {p.designation} · {p.status}
                                            </div>
                                            <hr />

                                            <div className="tooltip-row">
                                                <span>Slot</span>
                                                <span>{p.timeLabel}</span>
                                            </div>

                                            {hasRecords ? (
                                                <>
                                                    <div className="tooltip-row">
                                                        <span>Records</span>
                                                        <span>{p.recordCount}</span>
                                                    </div>

                                                    <div className="tooltip-row">
                                                        <span>Distance</span>
                                                        <span
                                                            className={`tooltip-distance ${p.dist > 0.5
                                                                    ? 'tooltip-distance--danger'
                                                                    : 'tooltip-distance--normal'
                                                                }`}
                                                        >
                                                            {p.dist.toFixed(2)} km
                                                        </span>
                                                    </div>
                                                </>
                                            ) : (
                                                <div className="tooltip-empty">No record</div>
                                            )}
                                        </div>
                                    );
                                }}
                            />

                            <Scatter data={scatterData} shape="square" isAnimationActive={false}>
                                {scatterData.map((p, i) => {
                                    let fill = '#ffffff';       // empty → white
                                    let stroke = '#e0e0e0';
                                    if (p.has && p.dist > 0.5) { fill = '#dc3545'; stroke = '#fff'; }
                                    else if (p.has) { fill = '#28a745'; stroke = '#fff'; }

                                    return (
                                        <Cell
                                            key={i}
                                            fill={fill}
                                            stroke={stroke}
                                            strokeWidth={0.5}
                                        />
                                    );
                                })}
                            </Scatter>
                        </ScatterChart>
                    </ResponsiveContainer>

                    <div className="reportrarel-chart-legend">
                        <span className="legend-item">
                            <span className="legend-box green" /> no deviation
                        </span>
                        <span className="legend-item">
                            <span className="legend-box red" /> &gt; 0.5 km (deviation)
                        </span>
                        <span className="legend-item">
                            <span className="legend-box white" style={{ border: '1px solid #e0e0e0' }} /> Empty record (no data)
                        </span>
                    </div>
                </div>
            )}
            <div className="reportrarel-table-monthlt-att-container">
                <table className="reportrarel-table-monthlt-att">
                    <thead>
                        {/* Row 1: Fixed columns (rowSpan=3) + Deviation spanning all hour cols */}
                        <tr>
                            <th rowSpan="3" className="sticky-col sno-col">S.No</th>
                            <th rowSpan="3" className="sticky-col name-col">Employee Name</th>
                            <th rowSpan="3" className="sticky-col name-col">Designation</th>
                            <th rowSpan="3" className="sticky-col loc-col">Location</th>
                            <th rowSpan="3" className="sticky-col loc-col">Attendance Status</th>
                            <th colSpan="48" className="deviation-header">
                                Deviation From Job Location
                            </th>
                        </tr>

                        {/* Row 2: Hour headers */}
                        <tr>
                            {Array.from({ length: 24 }, (_, i) => (
                                <th key={i + 1} colSpan="2" className="hour-header">
                                    {String(i).padStart(2, '0')}:00 – {String(i + 1).padStart(2, '0')}:00 hr
                                </th>
                            ))}
                        </tr>

                        {/* Row 3: 30-min sub-headers */}
                        <tr>
                            {Array.from({ length: 24 }, (_, i) => (
                                <React.Fragment key={`sub-${i}`}>
                                    <th className="sub-header">00 – 30 min</th>
                                    <th className="sub-header">30 – 60 min</th>
                                </React.Fragment>
                            ))}
                        </tr>
                    </thead>

                    <tbody>
                        {attendanceData.length > 0 ? (
                            attendanceData.map((employee, empIdx) => (
                                <tr key={employee.employeeUniqueId || empIdx} className="clickable-row">
                                    <td className="sticky-col sno-col">{empIdx + 1}</td>
                                    <td className="sticky-col name-col">{employee.employeeName}</td>
                                    <td className="sticky-col name-col">{employee.designation}</td>
                                    <td className="sticky-col loc-col">{employee.locationName}</td>
                                    <td className="sticky-col loc-col">{employee.attendanceStatus}</td>

                                    {employee.timeline?.map((hourBlock) =>
                                        hourBlock.slots?.map((slot) => {
                                            const firstRecord = slot.records?.[0];
                                            const rawValue = firstRecord?.distanceFromPreviousMeters;

                                            return (
                                                <td
                                                    key={slot.timeWindow}
                                                    className={`distance-cell ${rawValue ? "has-distance" : ""}`}
                                                    title={`${slot.timeWindow} | Records: ${slot.records?.length || 0} | Distance: ${rawValue ?? 0}`}
                                                >
                                                    {rawValue !== undefined && rawValue !== null ? `${rawValue} km` : "-"}
                                                </td>
                                            );
                                        })
                                    )}
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="54" className="no-data-cell">
                                    No data available. Please generate a report.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

        </div>
    );
};

export default EmployeeTracking;